import type { MouseEvent } from 'react';
import React, { useEffect } from 'react';
import { FavouriteButton } from '@noths/polaris-client-ribbons-design-system';
import { getProductImageURL, isKeyboardTriggeredEvent } from '@noths/polaris-client-utils';
import Image from 'next/image';

import { ImageLabel } from 'src/components/atoms/ImageLabel/ImageLabel';
import {
  NextButton,
  PreviousButton,
} from 'src/components/atoms/ImageNavigationButtons/ImageNavigationButtons';
import { ImageNumberLabel } from 'src/components/atoms/ImageNumberLabel/ImageNumberLabel';
import { PreventSwipeNavigation } from 'src/components/molecules/PreventSwipeNavigation/PreventSwipeNavigation';
import { ThumbnailsContainer } from 'src/components/molecules/Thumbnails/ThumbnailsContainer';
import type {
  NextOrPreviousPayload,
  OpenModalPayload,
  ProductImagesState,
  SetCurrentImagePayload,
} from 'src/components/organisms/ProductImagesCarousel/types';
import type { CatServiceAPIProduct } from 'src/services/cat-service-api/types/CatServiceAPIProduct';
import { PRODUCT_IMAGES_SECTION_LABEL_SINGLE } from './constants/copy';
import {
  PRODUCT_IMAGE_TABPANEL_CLASS,
  PRODUCT_IMAGES_TAB_ID_PREFIX,
  PRODUCT_IMAGES_TABPANEL_CONTAINER_ID,
  PRODUCT_IMAGES_TABPANEL_ID_PREFIX,
} from './constants/elementIds';
import { useImageLabel } from './useImageLabel';
import { useProductImagesCarousel } from './useProductImagesCarousel';
import * as styles from './ProductImagesCarousel.styles';

export interface ProductImagesCarouselStateProps {
  currentImage: ProductImagesState['currentImage'];
  favouriteProduct: boolean;
  navigatedBy: ProductImagesState['navigatedBy'];
  product: CatServiceAPIProduct;
}

export interface ProductImagesCarouselDispatchProps {
  onFavouriteClick: (productCode: number) => void;
  onNextImage: (payload: NextOrPreviousPayload) => void;
  onOpenModal: (payload: OpenModalPayload) => void;
  onPrevImage: (payload: NextOrPreviousPayload) => void;
  setCurrentImage: (payload: SetCurrentImagePayload) => void;
}

export type ProductImagesCarouselProps = ProductImagesCarouselStateProps &
  ProductImagesCarouselDispatchProps;

const handleImageClick = (
  e: MouseEvent<HTMLAnchorElement>,
  onOpenModal: ProductImagesCarouselDispatchProps['onOpenModal'],
) => {
  e.preventDefault();
  e.stopPropagation();
  onOpenModal({ location: 'Main image' });
};

export const ProductImagesCarousel = ({
  currentImage,
  favouriteProduct,
  navigatedBy,
  onFavouriteClick,
  onNextImage,
  onOpenModal,
  onPrevImage,
  product,
  setCurrentImage,
}: ProductImagesCarouselProps) => {
  const totalImages = product.images.length;
  const location = 'Main image';
  const [imageFocus, setImageFocus] = React.useState(false);
  const [hasInteractedWithCarousel, setHasInteractedWithCarousel] = React.useState(false);
  const [loadedImages, setImageLoaded] = React.useState<Record<number, boolean>>({});
  const imageLabelData = useImageLabel({
    isNew: product.isNew,
    salePercentage: product.salePercentage || 0,
    hasStock: product.purchasable,
    unavailable: !product.available && product.availability === 'suspended',
  });

  useEffect(() => {
    if (currentImage > 0) {
      // User has navigated past the first image
      setHasInteractedWithCarousel(true);
    }
  }, [currentImage]);

  const [carouselContainerRef, carouselItemRefs] = useProductImagesCarousel(
    (entry) => {
      const imageIndex = Number((entry.target as HTMLElement).dataset.itemIndex);
      if (currentImage != imageIndex) {
        setCurrentImage({ imageIndex, navigationType: 'swipe', location });
      }
    },
    currentImage,
    navigatedBy,
    totalImages,
  );

  return (
    <>
      <PreventSwipeNavigation aria-live="polite" css={[styles.imagesWrapper]}>
        <section aria-label={PRODUCT_IMAGES_SECTION_LABEL_SINGLE} css={[styles.carouselContainer]}>
          <div
            css={[
              styles.mainImageWrapperWithThumbnails,
              imageLabelData ? styles.topRoundedCorners : styles.allRoundedCorners,
            ]}
            data-image-focus={imageFocus}
            id={PRODUCT_IMAGES_TABPANEL_CONTAINER_ID}
            ref={carouselContainerRef}
          >
            {product.images.map(({ href: src, title }, i) => (
              <div
                aria-hidden={currentImage !== i}
                aria-labelledby={`${PRODUCT_IMAGES_TAB_ID_PREFIX}${i}`}
                className={PRODUCT_IMAGE_TABPANEL_CLASS}
                css={styles.carouselSlide}
                data-item-index={i}
                id={`${PRODUCT_IMAGES_TABPANEL_ID_PREFIX}${i}`}
                key={src}
                ref={(node) => {
                  carouselItemRefs.current[i] = node as HTMLElement;
                }}
                role="tabpanel"
              >
                <a
                  css={styles.mainImageLink}
                  href={getProductImageURL(src, 'original')}
                  onBlur={() => setImageFocus(false)}
                  onClick={(e) => handleImageClick(e, onOpenModal)}
                  onFocus={(e) => setImageFocus(isKeyboardTriggeredEvent(e))}
                  tabIndex={currentImage === i ? undefined : -1}
                >
                  <Image
                    alt={`${title}, ${i + 1} of ${totalImages}`}
                    css={styles.mainImage}
                    fill={true}
                    onLoad={() => setImageLoaded({ ...loadedImages, [i]: true })}
                    priority={i === 0}
                    src={getProductImageURL(
                      src,
                      i === 0 || hasInteractedWithCarousel ? 'original' : 'normal',
                    )}
                    unoptimized
                  />
                </a>
              </div>
            ))}
          </div>
          {totalImages > 1 && (
            <>
              <PreviousButton onClick={() => onPrevImage({ location, totalImages })} />
              <NextButton onClick={() => onNextImage({ location, totalImages })} />
              <ImageNumberLabel currentImage={currentImage + 1} totalImages={totalImages} />
            </>
          )}
          <div css={styles.favouriteButtonWrapper}>
            <FavouriteButton
              active={favouriteProduct}
              css={styles.favouriteButton}
              onClick={() => onFavouriteClick(product.code)}
              size="large"
            />
          </div>
        </section>
      </PreventSwipeNavigation>
      {imageLabelData && <ImageLabel {...imageLabelData} />}
      {totalImages > 1 && (
        <div css={styles.imageThumbnails}>
          <ThumbnailsContainer location={location} />
        </div>
      )}
    </>
  );
};
